import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Company } from '../../../../types/company.types';

interface Props {
  open: boolean;
  loading?: boolean;
  tenant: Company | null;

  handleDeleteTenant(): void;

  handleClose(): void;
}

const DeleteTenantDialog: React.FC<Props> = ({ open, loading, tenant, handleDeleteTenant, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const tenantName = `${tenant?.name ?? ''}`;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('deleteTenant')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>
            <Trans i18nKey={'deleteTenantWarning1'} values={{ tenantName }}>
              Should the tenant <strong>tenantName</strong> really be deleted?
            </Trans>
          </Typography>
          <Typography className={cx(classes.text, 'highlight')}>{t('deleteTenantWarning2')}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleDeleteTenant} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.warning': {
      color: theme.palette.warning.main,
      fontWeight: 'bold',
    },
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default DeleteTenantDialog;
